import type { UseFormRegisterReturn } from "react-hook-form";

import { Alert, Button, Input, Select, TextArea } from "@alpacahq/ui/old";

import type { OAuthClientCategory } from "~/v2/api/oauth";
import type { SubmitAppFormValues } from "./SubmitApp.schema";
import type {
  SubmitAppFieldErrors,
  SubmitAppMode,
  SubmitAppRedirectField,
} from "./SubmitApp.types";
import { MAX_REDIRECT_URIS } from "./SubmitApp.utils";

type ApplicationDetailsValues = Pick<
  SubmitAppFormValues,
  | "categories"
  | "description"
  | "name"
  | "privacy_policy"
  | "redirect_uri"
  | "terms_of_use"
  | "url"
>;

export interface ApplicationDetailsProps {
  mode: SubmitAppMode;
  values: ApplicationDetailsValues;
  fields: {
    name: UseFormRegisterReturn<"name">;
    description: UseFormRegisterReturn<"description">;
    url: UseFormRegisterReturn<"url">;
    termsOfUse: UseFormRegisterReturn<"terms_of_use">;
    privacyPolicy: UseFormRegisterReturn<"privacy_policy">;
  };
  redirects: SubmitAppRedirectField[];
  errors: Omit<SubmitAppFieldErrors, "screenshots">;
  canAddRedirect: boolean;
  onCategoriesChange: (categories: OAuthClientCategory[]) => void;
  onAddRedirect: () => void;
  onRemoveRedirect: (index: number) => void;
  onViewCredentials: () => void;
}

const CATEGORY_OPTIONS: Array<{
  label: string;
  value: OAuthClientCategory;
}> = [
  { label: "Crypto", value: "CRYPTO" },
  { label: "Algos and Strategies", value: "ALGOS_AND_STRATEGIES" },
  { label: "Social", value: "SOCIAL" },
  { label: "Charting", value: "CHARTING" },
  { label: "Market Data", value: "MARKET_DATA" },
];
const DEV_TOOLS_OPTION = {
  label: "Dev Tools",
  value: "DEV_TOOLS" as const,
};

export const ApplicationDetails = ({
  mode,
  values,
  fields,
  redirects,
  errors,
  canAddRedirect,
  onCategoriesChange,
  onAddRedirect,
  onRemoveRedirect,
  onViewCredentials,
}: ApplicationDetailsProps) => {
  const categoryOptions = values.categories.includes("DEV_TOOLS")
    ? [...CATEGORY_OPTIONS, DEV_TOOLS_OPTION]
    : CATEGORY_OPTIONS;

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-4">
        <h2 className="m-0 text-2xl">Application Details</h2>
        {mode === "edit" && (
          <Button type="button" onClick={onViewCredentials}>
            View App Credentials
          </Button>
        )}
      </div>

      <Alert
        variant="info"
        //title="Application Details"
        message="All Alpaca Connect apps require approval from Alpaca Compliance before going live. After creating your app, please submit the following application details below for review. Our team will contact you directly for additional information needed to proceed."
      />

      <Input
        {...fields.name}
        aria-invalid={Boolean(errors.name)}
        errorMsg={errors.name}
        id="submit-app-name"
        label="Name:"
        placeholder="Name of your app"
        value={values.name}
      />

      <Select<OAuthClientCategory>
        errorMsg={errors.categories}
        label="Category:"
        multiple={true}
        onChange={onCategoriesChange}
        options={categoryOptions}
        placeholder="Select a category"
        value={values.categories}
      />

      <TextArea
        {...fields.description}
        aria-invalid={Boolean(errors.description)}
        canResize={true}
        className="min-h-32"
        errorMsg={errors.description}
        id="submit-app-description"
        label="Description:"
        placeholder="Describe your app"
        value={values.description}
      />

      <Input
        {...fields.url}
        aria-invalid={Boolean(errors.url)}
        errorMsg={errors.url}
        id="submit-app-url"
        label="Application Website:"
        placeholder="A link to your app's homepage"
        value={values.url}
      />

      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-2">
          <span className="flex items-center justify-start gap-1 font-semibold text-gray-700 text-sm">
            Redirect URI:
          </span>
          {redirects.map((redirect, index) => {
            const redirectError =
              [
                redirect.error,
                index === redirects.length - 1
                  ? errors.redirectUris
                  : undefined,
              ]
                .filter(Boolean)
                .join(" ") || undefined;

            return (
              <div className="flex items-center gap-2" key={redirect.id}>
                <Input
                  {...redirect.input}
                  aria-label={`Redirect URI ${index + 1}`}
                  aria-invalid={Boolean(redirectError)}
                  errorMsg={redirectError}
                  id={`submit-app-redirect-${index}`}
                  placeholder="Add new redirect URI"
                  value={values.redirect_uri[index] ?? ""}
                />
                {redirects.length > MAX_REDIRECT_URIS && (
                  <button
                    aria-label={`Remove redirect URI ${index + 1}`}
                    className="rounded px-2 py-1 text-sm hover:bg-black/5"
                    onClick={() => onRemoveRedirect(index)}
                    type="button"
                  >
                    Remove
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <button
          className="self-start rounded px-2 py-1 hover:bg-black/5 disabled:cursor-not-allowed disabled:opacity-40"
          disabled={!canAddRedirect}
          onClick={onAddRedirect}
          type="button"
        >
          <span aria-hidden="true" className="text-lg">
            +
          </span>{" "}
          Add new redirect URI
        </button>
      </div>

      <Input
        {...fields.termsOfUse}
        aria-invalid={Boolean(errors.termsOfUse)}
        errorMsg={errors.termsOfUse}
        id="submit-app-terms"
        label="Terms of Use:"
        placeholder="A link to your app's Terms of Use"
        value={values.terms_of_use}
      />

      <Input
        {...fields.privacyPolicy}
        aria-invalid={Boolean(errors.privacyPolicy)}
        errorMsg={errors.privacyPolicy}
        id="submit-app-privacy"
        label="Privacy Policy:"
        placeholder="A link to your app's Privacy Policy"
        value={values.privacy_policy}
      />
    </section>
  );
};
